/**
 * Blueberries pricing model: the single source of truth used by both the
 * pricing cards and the interactive calculator.
 *
 * The FP&A License is mandatory for every customer. Data Enrichment is an
 * optional add-on.
 *
 * Two products:
 *   1. FP&A License        - $1,500 / month per seat. Required for everyone.
 *                            Offer: buy 1, get 2 free for year 1.
 *   2. Data Enrichment     - optional add-on, two plans (year-1 intro, then year-2):
 *        - Starter    ($50 → $100 / employee / month after year 1; 3 integrations
 *                      included, +$20/employee per extra integration stays).
 *        - Unlimited  ($100 → $200 / employee / month after year 1; unlimited
 *                      integrations).
 *
 * Custom plan: for teams whose needs don't fit the cards above (bespoke
 * integrations, larger orgs, special onboarding). No list price — quoted
 * per-account after a conversation, so it never enters computeQuote().
 *
 * Discounts:
 *   - No automatic volume discount. Any discount is granted per-account by an
 *     admin (the "overall discount"), applied on top of list price via Stripe.
 *   - The admin picks what it reduces: the whole subscription, or the Data
 *     Enrichment lines only (the FP&A seats then stay at the standard fee,
 *     which is what the free seats convert to after year 1).
 */

// ── FP&A License ─────────────────────────────────────────────────────────────
export const FPA_MONTHLY_PER_SEAT = 1500;
/** Offer: for every paid seat, 2 more are free for the first year (pay 1 of every 3). */
export const FPA_OFFER_PAID_RATIO = 3;
export const FPA_OFFER_LABEL = 'Buy 1, get 2 free for 1 year';

// ── Data Enrichment ──────────────────────────────────────────────────────────
/** Year-1 Starter rate (what Stripe bills for the first 12 months). */
export const DE_STARTER_PER_EMPLOYEE = 50;
/** Year-2 Starter rate (from month 13). */
export const DE_STARTER_Y2_PER_EMPLOYEE = 100;
export const DE_STARTER_INCLUDED_INTEGRATIONS = 3;
export const DE_EXTRA_INTEGRATION_PER_EMPLOYEE = 20;

/** Year-2 Unlimited rate (from month 13). Also the historical "list" price. */
export const DE_UNLIMITED_LIST_PER_EMPLOYEE = 200;
export const DE_UNLIMITED_Y2_PER_EMPLOYEE = DE_UNLIMITED_LIST_PER_EMPLOYEE;
/** Year-1 Unlimited rate (what Stripe bills for the first 12 months). */
export const DE_UNLIMITED_PROMO_PER_EMPLOYEE = 100;
export const DE_UNLIMITED_Y1_PER_EMPLOYEE = DE_UNLIMITED_PROMO_PER_EMPLOYEE;
/** @deprecated Prefer DE_UNLIMITED_Y1 / Y2 naming; kept for older call sites. */
export const DE_UNLIMITED_DISCOUNT_PCT = 0.5;

export type DePlan = 'STARTER' | 'UNLIMITED';

// ── Admin discount ───────────────────────────────────────────────────────────
/** What an admin discount reduces. Mirrors DiscountAppliesTo on the backend. */
export type DiscountAppliesTo = 'subscription' | 'data_enrichment';

/**
 * A per-period amount after an admin discount. `dataEnrichmentAmount` is the
 * Data Enrichment part of `total` for the same period; it is the only part
 * reduced when the discount applies to Data Enrichment (0 on a custom plan, so
 * nothing is reduced, exactly like the Stripe coupon).
 */
export function discountedAmount(
  total: number,
  dataEnrichmentAmount: number,
  percent: number | null | undefined,
  appliesTo: DiscountAppliesTo | null | undefined,
): number {
  if (!percent) return total;
  const base = appliesTo === 'data_enrichment' ? Math.min(dataEnrichmentAmount, total) : total;
  return Math.round((total - (base * percent) / 100) * 100) / 100;
}

/** " on Data Enrichment" for a Data Enrichment discount, else nothing. */
export function discountTargetSuffix(appliesTo: DiscountAppliesTo | null | undefined): string {
  return appliesTo === 'data_enrichment' ? ' on Data Enrichment' : '';
}

// ── Annual billing ───────────────────────────────────────────────────────────
/**
 * Pay for a year up front, get 20% off. The discount is BAKED INTO the annual
 * Stripe prices (no coupon), so admin per-user discounts still stack on top.
 */
export const ANNUAL_DISCOUNT_PCT = 0.2;

export type BillingInterval = 'month' | 'year';

/** Annual total for a monthly amount: 12 months less the annual discount. */
export function annualTotal(monthly: number): number {
  return Math.round(monthly * 12 * (1 - ANNUAL_DISCOUNT_PCT) * 100) / 100;
}

// ── FP&A after the free year ─────────────────────────────────────────────────
/**
 * The FP&A charge per billing period once the free seats become paid (from
 * month 13): every seat at the standard fee. Stripe keeps applying an ongoing
 * whole-subscription discount to it; a Data Enrichment discount leaves it at
 * full price. Mirrors fpaAfterFreeYearCents on the backend.
 */
export function fpaAfterFreeYearAmount(
  totalSeats: number,
  annual: boolean,
  discountPercent: number | null | undefined,
  appliesTo: DiscountAppliesTo | null | undefined,
): number {
  const monthly = totalSeats * FPA_MONTHLY_PER_SEAT;
  const period = annual ? annualTotal(monthly) : monthly;
  return discountedAmount(period, 0, discountPercent, appliesTo);
}

/** Plan-summary line for the FP&A charge after the free year (recorded in signed documents). */
export function fpaAfterFreeYearLine(
  quote: QuoteResult,
  annual: boolean,
  discountPercent: number | null | undefined,
  appliesTo: DiscountAppliesTo | null | undefined,
): string {
  const amount = fpaAfterFreeYearAmount(quote.fpaTotalSeats, annual, discountPercent, appliesTo);
  const discounted = !!discountPercent && appliesTo !== 'data_enrichment';
  return `FP&A from month 13: the ${quote.fpaFreeSeats} free seat${quote.fpaFreeSeats === 1 ? '' : 's'} become paid, ${quote.fpaTotalSeats} seats, ${formatUSD(amount)} / ${annual ? 'year' : 'month'}${discounted ? ` (after the ${discountPercent}% discount)` : ''}`;
}

/** Per-employee year-2 Data Enrichment rate for the selected plan. */
export function deY2PerEmployee(plan: DePlan): number {
  return plan === 'STARTER' ? DE_STARTER_Y2_PER_EMPLOYEE : DE_UNLIMITED_Y2_PER_EMPLOYEE;
}

/**
 * Year-2 Data Enrichment charge for the period (employees × Y2 rate, annualised
 * when needed). Extra integrations stay at +$20 and are not doubled.
 */
export function deAfterIntroAmount(quote: QuoteResult, employees: number, annual: boolean): number {
  const monthly = employees * deY2PerEmployee(quote.effectivePlan);
  return annual ? annualTotal(monthly) : monthly;
}

/** Plan-summary line for DE after the intro year (recorded in signed documents). */
export function deAfterIntroLine(quote: QuoteResult, employees: number, annual: boolean): string {
  const per = annual ? 'year' : 'month';
  const label = quote.effectivePlan === 'STARTER' ? 'Starter' : 'Unlimited';
  const amount = deAfterIntroAmount(quote, employees, annual);
  return `Data Enrichment ${label} from month 13: ${formatUSD(deY2PerEmployee(quote.effectivePlan))} / employee / month (${formatUSD(amount)} / ${per} for ${employees} employee${employees === 1 ? '' : 's'}; extra integrations unchanged)`;
}

// ── Custom plan ──────────────────────────────────────────────────────────────
// No numbers on purpose: custom deals are quoted per-account by the team, so
// the UI shows these labels and routes to a conversation instead of checkout.
export const CUSTOM_PLAN_NAME = 'Custom';
export const CUSTOM_PLAN_TAGLINE = 'Tailored to your needs';
export const CUSTOM_PLAN_PRICE_LABEL = 'Custom pricing';
export const CUSTOM_PLAN_BLURB =
  'A plan built around your team — your integrations, your workflows, your scale. Tell us what you need and we will shape Blueberries around it.';

/**
 * Integrations a Starter customer can pick from (3 included, extras billed).
 * Not exhaustive — customers can also add their own custom integrations in the
 * calculator.
 */
export const INTEGRATION_OPTIONS = [
  // Workspace & communication
  'Google Workspace',
  'Google Calendar',
  'Google Meet',
  'Microsoft 365',
  'Microsoft Teams',
  'Slack',
  'Zoom',
  // Project & work management
  'Monday.com',
  'Notion',
  'Asana',
  'Jira',
  'Airtable',
  'ClickUp',
  'Trello',
  'Smartsheet',
  // CRM & customer
  'HubSpot',
  'Salesforce',
  'Pipedrive',
  'Zoho CRM',
  'Zendesk',
  'Intercom',
  // Accounting & ERP
  'QuickBooks',
  'Xero',
  'NetSuite',
  'Sage Intacct',
  'FreshBooks',
  'Bill.com',
  // Payments & spend
  'Stripe',
  'PayPal',
  'Plaid',
  'Brex',
  'Ramp',
  'Expensify',
  // Payroll & HR
  'Gusto',
  'Rippling',
  'ADP',
  'BambooHR',
  'Workday',
  'Deel',
  // Data, storage & commerce
  'Snowflake',
  'Tableau',
  'Power BI',
  'Dropbox',
  'Box',
  'Shopify',
  'AppFolio',
] as const;

export interface QuoteInput {
  employees: number;
  /** FP&A License selected. Mandatory for everyone, so always true. */
  fpa: boolean;
  /** Number of FP&A seats (people who get the license). */
  fpaSeats: number;
  /** Data Enrichment selected (optional add-on). */
  de: boolean;
  /** Which Data Enrichment plan. Both available to any team size. */
  dePlan: DePlan;
  /** Total integrations chosen (Starter only; 3 are included, extras are billed). */
  integrations: number;
}

export interface QuoteResult {
  // FP&A
  /** Paid FP&A seats the customer buys (what fpaMonthly is charged on). */
  fpaSeats: number;
  fpaMonthly: number;
  /** Free FP&A seats granted for year 1: 2 per paid seat (buy 1, get 2). */
  fpaFreeSeats: number;
  /** Total FP&A seats the customer gets for year 1 (paid + free). */
  fpaTotalSeats: number;

  // Data Enrichment
  effectivePlan: DePlan;
  extraIntegrations: number;
  deMonthly: number;

  // Combined
  subtotal: number;
  total: number; // monthly recurring
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function computeQuote(input: QuoteInput): QuoteResult {
  const employees = Math.max(0, Math.floor(input.employees || 0));

  // ── FP&A ──
  // fpaSeats = paid seats. Buy 1 paid seat, get 2 free for year 1 (scales:
  // buy N, get 2N free). The customer is charged for the paid seats only.
  const fpaSeats = input.fpa ? Math.max(0, Math.floor(input.fpaSeats || 0)) : 0;
  const fpaMonthly = roundMoney(fpaSeats * FPA_MONTHLY_PER_SEAT);
  const fpaFreeSeats = fpaSeats * (FPA_OFFER_PAID_RATIO - 1);
  const fpaTotalSeats = fpaSeats + fpaFreeSeats;

  // ── Data Enrichment ──
  // Starter and Unlimited are both available to any team size.
  const effectivePlan: DePlan =
    input.de && input.dePlan === 'STARTER' ? 'STARTER' : 'UNLIMITED';

  let extraIntegrations = 0;
  let deMonthly = 0;

  if (input.de) {
    if (effectivePlan === 'STARTER') {
      extraIntegrations = Math.max(0, Math.floor(input.integrations || 0) - DE_STARTER_INCLUDED_INTEGRATIONS);
      const perEmployee = DE_STARTER_PER_EMPLOYEE + extraIntegrations * DE_EXTRA_INTEGRATION_PER_EMPLOYEE;
      deMonthly = roundMoney(employees * perEmployee);
    } else {
      deMonthly = roundMoney(employees * DE_UNLIMITED_PROMO_PER_EMPLOYEE);
    }
  }

  // ── Combined ──
  const subtotal = roundMoney(fpaMonthly + deMonthly);
  const total = subtotal;

  return {
    fpaSeats,
    fpaMonthly,
    fpaFreeSeats,
    fpaTotalSeats,
    effectivePlan,
    extraIntegrations,
    deMonthly,
    subtotal,
    total,
  };
}

export function formatUSD(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}
